import { TimelineBlock } from "./block";
import { ConcurrentLabel } from "./concurrency";
import { type TimelineProcedure, type TimelineStep, useTimeline } from "./context";

function Body({ procedure, titleClassName }: { procedure: TimelineProcedure; titleClassName?: string }) {
  return (
    <>
      <TimelineBlock.Row>
        <TimelineBlock.Title className={titleClassName} title={procedure.title}>
          {procedure.title}
        </TimelineBlock.Title>
      </TimelineBlock.Row>
      {procedure.detail != null && <TimelineBlock.Detail>{procedure.detail}</TimelineBlock.Detail>}
      {procedure.badge}
    </>
  );
}

/** Matches `gap-1.5`, so steps sit as far apart as the lanes within one. */
const TILE_GAP_PX = 6;

/** Fills the width it is given and takes exactly the height of its duration. */
function TiledProcedure({ procedure, height }: { procedure: TimelineProcedure; height: number }) {
  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      <TimelineBlock variant={procedure.variant} accent={procedure.accent} style={{ height }}>
        <Body procedure={procedure} />
      </TimelineBlock>
    </div>
  );
}

/**
 * Full width, and free to grow past its duration. It WRAPS its title rather
 * than truncating: in a ~250px column what gets cut is exactly what tells two
 * procedures apart ("Gaze Stability Exercises — Vertical/Horizontal Plane").
 */
function StackedProcedure({ procedure, reservedMinutes }: { procedure: TimelineProcedure; reservedMinutes: number }) {
  const { meta } = useTimeline();
  return (
    <div className="flex min-w-0 flex-col">
      <TimelineBlock
        variant={procedure.variant}
        accent={procedure.accent}
        style={{ minHeight: meta.scale(reservedMinutes) }}
      >
        <Body procedure={procedure} titleClassName="whitespace-normal break-words" />
      </TimelineBlock>
    </div>
  );
}

/**
 * Concurrent procedures side by side, each filling the step reservation less
 * the same gap that separates them, so consecutive steps never touch.
 */
export function TiledStep({ step }: { step: TimelineStep }) {
  const { meta } = useTimeline();
  const height = Math.max(0, meta.scale(step.costMinutes) - TILE_GAP_PX);
  return (
    <div className="flex min-w-0 gap-1.5" style={{ height }}>
      {step.procedures.map((procedure) => (
        <TiledProcedure key={procedure.id} procedure={procedure} height={height} />
      ))}
    </div>
  );
}

/**
 * Concurrent procedures stacked under one label, for a column too narrow to
 * tile them. Stacking makes a concurrent step read TALLER than the time it
 * occupies — the honest alternative was showing neither title.
 */
export function StackedStep({ step }: { step: TimelineStep }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {step.procedures.length > 1 && <ConcurrentLabel />}
      {step.procedures.map((procedure) => (
        <StackedProcedure key={procedure.id} procedure={procedure} reservedMinutes={step.costMinutes} />
      ))}
    </div>
  );
}
